<script lang="ts">
	import { getContextScope } from '$lib/js/helpers/i18n'
	import { registerClickEvent } from '$lib/js/ga4/events'
	import { storeChatModal } from '$lib/js/stores'
	import { page } from '$app/stores'
	import { t } from 'svelte-i18n-lingui'
	import type { HelpCard } from '$lib/js/types'
	import { openChatwootChat } from '$lib/js/chatwoot/helpers'
	import { slide, fade } from 'svelte/transition'
	import type { TransitionConfig } from 'svelte/transition'

	const scope = getContextScope()

	function slideAndFade(node: Element, { duration = 300 }: { duration?: number } = {}): TransitionConfig {
		const slideTransition = slide(node, { duration })
		const fadeTransition = fade(node, { duration })

		return { duration, css: (t) => `${slideTransition.css(t)}${fadeTransition.css(t)}`}
	}

	let activeCard: undefined | HelpCard = undefined
	let settings = $page.data.settings
	let wa_link = `https://wa.me/${settings.whatsapp_number}/?text=`

	$: innerWidth = 0

	$: if (innerWidth <= 1024) {
		wa_link = `https://wa.me/${settings.whatsapp_number}/?text=`
	} else {
		wa_link = `https://web.whatsapp.com/send?l=${scope}&phone=${settings.whatsapp_number}&text=`
	}

	let HelpCards: Array<HelpCard> = [
		{
			heading: $t`I want to buy in Bixoto`,
			heading_icon: 'icon-shop',
			action: 'presell',
			subactions: [
				{ title: $t`More information about a product`, link: wa_link, target: '_blank' },
				{ title: $t`Go to FAQs`, link: 'centro-de-ayuda/preventa' },
				{ title: $t`Know more about Bixoto`, link: 'quienes-somos' },
				{ title: $t`View our clients opinions`, link: 'opiniones' },
			],
		},
		{
			heading: $t`Locate my order`,
			heading_icon: 'icon-van',
			action: 'tracking',
			subactions: [
				{
					title: $t`I entered incorrect data in my order or it's missing information`,
					link: 'centro-de-ayuda/seguimiento/corregir-datos',
				},
				{
					title: $t`I want to know the status of my order`,
					link: 'centro-de-ayuda/seguimiento/estado-pedido',
				},
				{
					title: $t`I want to cancel my order`,
					link: 'centro-de-ayuda/seguimiento/cancelacion-pedido',
				},
			],
		},
		{
			heading: $t`Postsale help`,
			heading_icon: 'icon-box',
			action: 'postsell',
			subactions: [
				{ title: $t`Request my invoice`, link: 'centro-de-ayuda/postventa/solicitar-factura' },
				{
					title: $t`I have a problem with my order or want to make a return (withdrawal)`,
					link: 'centro-de-ayuda/postventa/incidencias',
				},
			],
		},
	]

	function loadChatwootsChat() {
		$storeChatModal = false
		openChatwootChat()
	}
</script>

<svelte:window bind:innerWidth />

<div class="chat-modal" transition:slideAndFade={{ duration: 200 }}>
	<div class="chat-modal__inner">
		<div class="chat-modal__top">
			<h2 class="head-4">{$t`Welcome to Bixoto!`}</h2>
		</div>
		<p>{$t`We are here to help you. Please tell us what you need today:`}</p>

		<div class="chat-modal__cards">
			{#if !activeCard}
				{#each HelpCards as card}
					<button
						class="card-btn"
						on:click={() => {
							activeCard = card
							registerClickEvent(card.action, 'chat')
						}}
					>
						<div class="chat-modal__cards__text">
							<span class="icon {card.heading_icon}" />
							{card.heading}
						</div>
						<span class="icon icon-arr-right" />
					</button>
				{/each}
			{:else}
				<button class="back" on:click={() => (activeCard = undefined)}>
					<span class="icon icon-arr-left" />
					<span class="icon {activeCard.heading_icon}" />
					{activeCard.heading}
				</button>

				{#each activeCard.subactions as subaction}
					<a
						class="card-btn"
						target={subaction?.target}
						rel="noopener noreferrer"
						href={subaction?.target ? subaction.link : `/${scope}/${subaction.link}`}
						on:click={() => ($storeChatModal = false)}
					>
						{subaction.title} <span class="icon icon-arr-right" />
					</a>
				{/each}
			{/if}
		</div>

		<div class="chat-modal__bottom">
			<div class="chat-modal__buttons">
				<a
					target="_blank"
					rel="noopener noreferrer"
					href={wa_link}
					class="whatsapp"
					on:click={() => registerClickEvent('whatsapp', 'chat')}
				>
					{$t`Open WhatsApp`}<span class="icon icon-whatsapp" />
				</a>
	
				<button class="chat u-btn" on:click={() => loadChatwootsChat()}>
					{$t`Open chat`}<span class="icon icon-chat" />
				</button>
			</div>
	
			<p class="opentimes">{$t`Opening hours: 9-19 Mon/Fr`}</p>
		</div>
	</div>
</div>

<style lang="scss">
	.chat-modal {
		position: fixed;
		bottom: 100px;
		right: 20px;
		padding: 18px;
		background: white;
		border-radius: 15px;
		box-shadow: 0px 0px 17px 5px rgba(0, 0, 0, 0.1);
		max-width: calc(100% - 76px);
		z-index: 9;

		@media (min-width: 769px) {
			bottom: 100px;
			right: 30px;
			padding: 25px;
			max-width: 340px;
		}

		@media (min-width: 1500px) {
			bottom: 104px;
			right: 20px;
		}

		&__cards {
			display: flex;
			flex-direction: column;
			align-items: flex-start;
			margin-top: 15px;
			min-height: 210px;
			border-bottom: 1px solid var(--grey);

			.back {
				margin-bottom: 15px;
				color: var(--grey--b);

				&:hover {
					.icon-arr-left {
						transform: translateX(-4px);
					}
				}

				span {
					margin-right: 5px;
					font-size: 14px;
				}

				.icon-arr-left {
					display: inline-block;
					transition: all linear 0.125s;
					font-size: 10px;
					margin-left: 4px;
				}
			}

			.card-btn {
				display: flex;
				justify-content: space-between;
				align-items: center;
				gap: 12px;
				text-align: left;
				margin-bottom: 10px;
				box-shadow: 2px 2px 6px 3px rgba(0, 0, 0, 0.08);
				border-radius: 10px;
				width: 100%;
				padding: 10px 15px;
				background-color: var(--white);
				color: black;
				line-height: 1.4;

				&:hover {
					.icon-arr-right {
						transform: translateX(4px);
					}
				}

				.icon-arr-right {
					transition: all linear 0.125s;
					font-size: 10px;
				}
			}

			a.card-btn {
				width: calc(100% - 30px);
			}

			&__text {
				span {
					font-size: 16px;
					margin-right: 6px;
				}
			}
		}

		&__buttons {
			display: flex;
			align-items: center;
			gap: 10px;
			padding-top: 15px;


			button.chat {
				flex-grow: 1;
				display: flex;
				align-items: center;
				justify-content: center;
				border-radius: 8px;
				padding: 4px 10px;
				span {
					font-weight: 400;
					font-size: 18px;
					margin-left: 8px;
				}
			}

			.whatsapp {
				flex-grow: 1;
				display: flex;
				align-items: center;
				justify-content: center;
				color: white;
				background: green;
				border: 2px solid green;
				padding: 4px 10px;
				border-radius: 10px;
				font-size: 12px;
				font-weight: 700;

				@media (min-width: 769px) {
					font-size: 13px;
				}

				span {
					font-weight: 400;
					font-size: 18px;
					margin-left: 10px;
				}

				&:hover {
					background: white;
					color: green;
				}
			}
		}

		.opentimes {
			color: var(--grey--b);
			margin-top: 15px;
		}
	}
</style>
